// Picks the DeviceBridge for the shell the app is running in:
//   Electron (preload exposes window.stationHost) → ElectronBridge
//   Capacitor native                              → CapacitorBridge (loaded on demand)
//   anything else (a browser)                     → WebBridge scanning + MockBridge printing

import {
  combineBridges,
  configureZxing,
  ElectronBridge,
  MockBridge,
  WebBridge,
  type DeviceBridge,
} from '@enp-suite/bridge';
import zxingWasmUrl from 'zxing-wasm/reader/zxing_reader.wasm?url';
import { initPrinterTarget, loadPrinterTarget } from './printer-settings.js';

interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
}

function isCapacitorNative(): boolean {
  const cap = (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
  return cap?.isNativePlatform?.() === true;
}

export async function selectBridge(): Promise<DeviceBridge> {
  // The decoder's .wasm ships with the app (stations scan offline); never from a CDN.
  configureZxing({ wasmUrl: new URL(zxingWasmUrl, document.baseURI).href });

  const host = window.stationHost;
  if (host?.platform === 'electron') {
    // The shell prints only to the printer in its own settings (CR3-12).
    await initPrinterTarget();
    return new ElectronBridge(host, { getTarget: loadPrinterTarget });
  }
  if (isCapacitorNative()) {
    const { CapacitorBridge } = await import('@enp-suite/bridge/capacitor');
    return new CapacitorBridge({ getTarget: loadPrinterTarget });
  }
  return combineBridges({ scanning: new WebBridge(), printing: new MockBridge() });
}
